import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import Button, { type ButtonProps } from '@segment/matcha/components/Button'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import { trackButtonClick } from '@segment/tracking/scripts'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `See Twilio Segment in action`,
  description: `Learn how you can start connecting your data with Twilio Segment. Explore our plans to find the one that’s right for your team.`,
}

type Button = {
  text: string
  href: string
  variant: ButtonProps[`variant`]
}

const buttons: Button[] = [
  {
    text: `Take the interactive tour`,
    href: `/product-tour?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2024-05-22-DG-good-data-campaign_20240522&utm_content=product-tour `,
    variant: `filled`,
  },
  {
    text: `Pricing`,
    href: `/pricing?utm_source=good-data-hub&utm_medium=referral&utm_campaign=all_ic_2024-05-22-DG-good-data-campaign_20240522&utm_content=pricing-page`,
    variant: `outlined`,
  },
]

const CtaSection: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <SectionBase variant="noCopy">
      <Section.Contents>
        <StyledCopyTextBase centered={true}>
          <StyledCopyTextHeading>{copy.heading}</StyledCopyTextHeading>
          <CopyText.Description tag="p">{copy.description}</CopyText.Description>
          <ButtonsWrapper>
            {buttons.map((button) => (
              <Button
                key={button.text}
                size="large"
                color="primary"
                variant={button.variant}
                href={getAbsoluteUrl(button.href, pageConfig)}
                onClick={() => trackButtonClick({ buttonText: button.text, targetUrl: button.href })}
              >
                {button.text}
              </Button>
            ))}
          </ButtonsWrapper>
        </StyledCopyTextBase>
      </Section.Contents>
    </SectionBase>
  )
}

const StyledCopyTextHeading = styled(CopyText.Heading)`
  margin-bottom: 24px;
`

const StyledCopyTextBase = styled(CopyTextBase)`
  max-width: 612px;
  margin: 0 auto;
`

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-top: 20px;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    flex-direction: column;
  }
`

export default CtaSection
